import React, { useState, useEffect } from 'react';
import { TrendingUp, Search, Target, AlertCircle, ChevronDown, ChevronUp, BarChart3, DollarSign, MapPin, Calendar, LineChart, RefreshCw, Download, Save, Grid3x3, Filter, X, Zap, TrendingDown, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis } from 'recharts';
import { DataForSEOClient } from '../lib/dataforseo';
import { getGoogleAdsClient, getDateRange } from '../lib/google-ads-api-client';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface KeywordData {
  id: string;
  keyword: string;
  location_code: number;
  location_name: string | null;
  language_code: string;
  search_volume: number | null;
  cpc: number | null;
  competition: number | null;
  competition_level: string | null;
  keyword_difficulty: number | null;
  search_intent: string | null;
  monthly_searches: any;
  last_updated: string | null;
  dataforseo_keyword_opportunities?: {
    opportunity_score: number;
    opportunity_type: string;
    priority: string;
  };
}

interface Location {
  location_code: number;
  location_name: string;
  location_type: string;
}

interface TrendData {
  month: string;
  search_volume: number;
}

interface KeywordMetrics {
  keyword: string;
  search_volume: number;
  cpc: number;
  competition_level: string;
  keyword_difficulty: number;
  search_intent: string;
  opportunity_score: number;
  opportunity_type: string;
  priority: string;
  location_name: string;
  monthly_trend?: TrendData[];
}

export default function KeywordDemandPage() {
  const [keywordData, setKeywordData] = useState<KeywordData[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'volume' | 'opportunity'>('opportunity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<number | 'all'>('all');
  const [selectedKeywordForTrends, setSelectedKeywordForTrends] = useState<string | null>(null);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [timeRange, setTimeRange] = useState<'3' | '6' | '12'>('6');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ current: 0, total: 0, status: '' });
  
  // Advanced filtering state
  const [dateRangeFilter, setDateRangeFilter] = useState<{
    enabled: boolean;
    startDate: string;
    endDate: string;
  }>({ enabled: false, startDate: '', endDate: '' });
  const [multiLocationMode, setMultiLocationMode] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [minVolume, setMinVolume] = useState<number>(0);
  const [maxCpc, setMaxCpc] = useState<number>(999);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // New interactive features state
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [searchIntentFilter, setSearchIntentFilter] = useState<string[]>([]);
  const [difficultyRange, setDifficultyRange] = useState<[number, number]>([0, 100]);
  const [showScatterPlot, setShowScatterPlot] = useState(false);
  const [visualizationMode, setVisualizationMode] = useState<'table' | 'scatter' | 'grid'>('table');
  const [savedPresets, setSavedPresets] = useState<any[]>([]);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  
  // Year-over-year comparison state
  const [comparisonMode, setComparisonMode] = useState<'standard' | 'yoy'>('standard');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [comparisonData, setComparisonData] = useState<{
    currentYear: TrendData[];
    previousYear: TrendData[];
    percentChange: number;
    absoluteChange: number;
  } | null>(null);
  
  // Google Ads integration
  const [viewMode, setViewMode] = useState<'dataforseo' | 'google-ads'>('dataforseo');
  const [googleAdsKeywords, setGoogleAdsKeywords] = useState<any[]>([]);
  const [loadingGoogleAds, setLoadingGoogleAds] = useState(false);
  const [collectingGoogleAds, setCollectingGoogleAds] = useState(false);
  const [googleAdsDateRange, setGoogleAdsDateRange] = useState<{ startDate: string; endDate: string }>(getDateRange(30));
  const { company, setCompany } = useGoogleAdsCompany();

  // Custom keywords management
  const [showAddKeywordModal, setShowAddKeywordModal] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [customKeywords, setCustomKeywords] = useState<string[]>([]);
  const [bulkKeywords, setBulkKeywords] = useState('');

  useEffect(() => {
    fetchLocations();
    fetchKeywordData();
    loadCustomKeywords();
    if (viewMode === 'google-ads') {
      fetchGoogleAdsKeywords();
    }
  }, [selectedLocation, viewMode]);

  // Core HVAC keywords to collect for each location
  const CORE_KEYWORDS = [
    'hvac repair', 'ac repair', 'air conditioning repair', 'furnace repair',
    'heating repair', 'ac installation', 'hvac installation', 'ac maintenance',
    'hvac maintenance', 'emergency hvac repair', 'emergency ac repair',
    '24 hour ac repair', 'ac not cooling', 'air conditioner not working',
    'furnace not heating', 'ac leaking water', 'commercial hvac repair',
    'ductwork repair', 'heat pump repair', 'air handler repair'
  ];

  // Load custom keywords from localStorage
  async function loadCustomKeywords() {
    try {
      const stored = localStorage.getItem('customKeywords');
      if (stored) {
        setCustomKeywords(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading custom keywords:', error);
    }
  }

  // Save custom keywords to localStorage
  function saveCustomKeywords(keywords: string[]) {
    localStorage.setItem('customKeywords', JSON.stringify(keywords));
    setCustomKeywords(keywords);
  }

  // Add a single keyword
  function addKeyword() {
    const trimmed = newKeyword.trim().toLowerCase();
    if (!trimmed) return;
    
    const allKeywords = [...CORE_KEYWORDS, ...customKeywords];
    if (allKeywords.includes(trimmed)) {
      alert('This keyword already exists');
      return;
    }
    
    const updated = [...customKeywords, trimmed];
    saveCustomKeywords(updated);
    setNewKeyword('');
  }

  // Add bulk keywords
  function addBulkKeywords() {
    const keywords = bulkKeywords
      .split('\n')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);
    
    const allKeywords = [...CORE_KEYWORDS, ...customKeywords];
    const newKeywords = keywords.filter(k => !allKeywords.includes(k));
    
    if (newKeywords.length === 0) {
      alert('All keywords already exist');
      return;
    }
    
    const updated = [...customKeywords, ...newKeywords];
    saveCustomKeywords(updated);
    setBulkKeywords('');
    alert(`Added ${newKeywords.length} new keyword(s)`);
  }

  // Remove a custom keyword
  function removeCustomKeyword(keyword: string) {
    const updated = customKeywords.filter(k => k !== keyword);
    saveCustomKeywords(updated);
  }

  // Get all keywords (core + custom)
  function getAllKeywords() {
    return [...CORE_KEYWORDS, ...customKeywords];
  }

  async function fetchLocations() {
    try {
      const { data, error } = await supabase
        .from('dataforseo_locations')
        .select('location_code, location_name, location_type')
        .eq('is_active', true)
        .eq('location_type', 'city')
        .order('location_name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }

  async function fetchGoogleAdsKeywords() {
    setLoadingGoogleAds(true);
    try {
      const { data: customerData } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (customerData) {
        const range = getDateRange(30);
        const { data } = await supabase
          .from('google_ads_keyword_performance')
          .select('*')
          .gte('date', range.startDate)
          .lte('date', range.endDate)
          .order('impressions', { ascending: false });

        if (data) {
          // Aggregate by keyword text
          const aggregated = new Map<string, any>();
          data.forEach(row => {
            const key = row.keyword_text?.toLowerCase() || '';
            if (!aggregated.has(key)) {
              aggregated.set(key, {
                keyword: row.keyword_text,
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                ctr: 0,
                avgCpc: 0,
                qualityScore: row.quality_score || 0,
                currentBid: (row.cpc_bid_micros || 0) / 1000000
              });
            }
            const entry = aggregated.get(key);
            entry.impressions += row.impressions || 0;
            entry.clicks += row.clicks || 0;
            entry.cost += (row.cost_micros || 0) / 1000000;
            entry.conversions += row.conversions || 0;
          });

          // Calculate derived metrics
          aggregated.forEach(entry => {
            entry.ctr = entry.impressions > 0 ? (entry.clicks / entry.impressions) * 100 : 0;
            entry.avgCpc = entry.clicks > 0 ? entry.cost / entry.clicks : 0;
          });

          setGoogleAdsKeywords(Array.from(aggregated.values()));
        }
      }
    } catch (error) {
      console.error('Error fetching Google Ads keywords:', error);
    } finally {
      setLoadingGoogleAds(false);
    }
  }

  async function collectGoogleAdsData() {
    setCollectingGoogleAds(true);
    try {
      const { data: customerData, error: custError } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (custError) throw custError;
      if (!customerData) throw new Error('No active Google Ads customer configured');

      const customerId =
        customerData.customer_id ||
        customerData.customerId ||
        customerData.customerid ||
        customerData.customer ||
        '';

      if (!customerId) throw new Error('Missing customerId in google_ads_customer_mapping');

      const client = getGoogleAdsClient();

      // Collect keyword metrics and search terms for selected date range
      await client.collectKeywordMetrics(customerId, {
        dateRange: { startDate: googleAdsDateRange.startDate, endDate: googleAdsDateRange.endDate },
      });

      await client.collectSearchTerms(customerId, {
        dateRange: { startDate: googleAdsDateRange.startDate, endDate: googleAdsDateRange.endDate },
      });

      // Refresh table from Supabase after collection
      await fetchGoogleAdsKeywords();
    } catch (error) {
      console.error('Error collecting Google Ads data:', error);
    } finally {
      setCollectingGoogleAds(false);
    }
  }

  async function fetchKeywordData() {
    setLoading(true);
    try {
      let query = supabase
        .from('dataforseo_keywords')
        .select(`
          *,
          dataforseo_keyword_opportunities (
            opportunity_score,
            opportunity_type,
            priority
          )
        `);

      if (selectedLocation !== 'all') {
        query = query.eq('location_code', selectedLocation);
      }

      query = query.order('search_volume', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      setKeywordData(data || []);
    } catch (error) {
      console.error('Error fetching keyword data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshKeywordData() {
    setIsRefreshing(true);
    setRefreshProgress({ current: 0, total: 0, status: 'Initializing...' });

    try {
      // Get all active locations
      const { data: allLocations, error: locError } = await supabase
        .from('dataforseo_locations')
        .select('location_code, location_name')
        .eq('is_active', true)
        .eq('location_type', 'city');

      if (locError) throw locError;

      const locationsToProcess = allLocations || [];
      const totalSteps = locationsToProcess.length;

      setRefreshProgress({ current: 0, total: totalSteps, status: 'Starting data collection...' });

      // Get DataForSEO credentials from environment
      const dataforSeoLogin = import.meta.env.VITE_DATAFORSEO_LOGIN;
      const dataforSeoPassword = import.meta.env.VITE_DATAFORSEO_PASSWORD;

      if (!dataforSeoLogin || !dataforSeoPassword) {
        throw new Error('DataForSEO credentials not configured. Please add VITE_DATAFORSEO_LOGIN and VITE_DATAFORSEO_PASSWORD to .env');
      }

      const client = new DataForSEOClient({
        login: dataforSeoLogin,
        password: dataforSeoPassword,
      });

      // Get all keywords to collect
      const allKeywords = getAllKeywords();

      // Process each location
      for (let i = 0; i < locationsToProcess.length; i++) {
        const location = locationsToProcess[i];
        setRefreshProgress({
          current: i + 1,
          total: totalSteps,
          status: `Collecting data for ${location.location_name.split(',')[0]}...`,
        });

        try {
          // Collect search volume data
          const volumeData = await client.getKeywordSearchVolume(
            allKeywords,
            location.location_code,
            'en'
          );

          // Collect difficulty data
          const difficultyData = await client.getKeywordDifficulty(allKeywords, 'en');

          // Combine and format data
          const keywordsToInsert = volumeData.map(kw => {
            const difficultyInfo = difficultyData.find(d => d.keyword === kw.keyword);
            
            // Format monthly_searches for database
            const monthlySearches = kw.monthly_searches?.map(ms => ({
              month: `${ms.year}-${String(ms.month).padStart(2, '0')}`,
              search_volume: ms.search_volume
            })) || [];

            // DataForSEO returns competition as numeric (0-1) and competition_level as string
            // Ensure we're sending the correct types to the database
            const competitionNumeric = typeof kw.competition === 'number' ? kw.competition : 0;
            const competitionLevel = (kw.competition_level || 'UNKNOWN').toUpperCase();

            return {
              keyword: kw.keyword,
              location_code: location.location_code,
              location_name: location.location_name,
              language_code: 'en',
              search_volume: kw.search_volume || 0,
              cpc: kw.cpc || 0,
              competition: competitionNumeric,  // NUMERIC 0.0-1.0
              competition_level: competitionLevel,  // STRING: LOW/MEDIUM/HIGH
              keyword_difficulty: difficultyInfo?.keyword_difficulty || null,
              search_intent: difficultyInfo?.search_intent || null,
              monthly_searches: monthlySearches,
              last_updated: new Date().toISOString(),
            };
          });

          // Insert into database
          const { error: insertError } = await supabase
            .from('dataforseo_keywords')
            .upsert(keywordsToInsert, {
              onConflict: 'keyword,location_code,language_code',
              ignoreDuplicates: false,
            });

          if (insertError) {
            console.error(`Error inserting data for ${location.location_name}:`, insertError);
          }

          // Small delay to respect API rate limits
          await new Promise(resolve => setTimeout(resolve, 1000));

        } catch (error) {
          console.error(`Error processing ${location.location_name}:`, error);
          // Continue with next location even if one fails
        }
      }

      setRefreshProgress({
        current: totalSteps,
        total: totalSteps,
        status: 'Complete! Reloading data...',
      });

      // Reload the page data
      await fetchKeywordData();
      
      // Reset after a short delay
      setTimeout(() => {
        setIsRefreshing(false);
        setRefreshProgress({ current: 0, total: 0, status: '' });
      }, 2000);

    } catch (error: any) {
      console.error('Error refreshing keyword data:', error);
      setRefreshProgress({
        current: 0,
        total: 0,
        status: `Error: ${error.message}`,
      });
      setTimeout(() => {
        setIsRefreshing(false);
        setRefreshProgress({ current: 0, total: 0, status: '' });
      }, 5000);
    }
  }

  async function fetchTrendData(keyword: string, locationCode: number) {
    try {
      if (comparisonMode === 'yoy' && customStartDate && customEndDate) {
        await fetchYearOverYearComparison(keyword, locationCode);
      } else {
        // Standard trend view
        const monthsAgo = parseInt(timeRange);
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - monthsAgo);

        const { data, error } = await supabase
          .from('dataforseo_keyword_trends')
          .select('month, search_volume')
          .eq('keyword', keyword)
          .eq('location_code', locationCode)
          .gte('month', startDate.toISOString().split('T')[0])
          .order('month', { ascending: true });

        if (error) throw error;

        const formattedData = (data || []).map(item => ({
          month: new Date(item.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          search_volume: item.search_volume || 0,
        }));

        setTrendData(formattedData);
        setComparisonData(null);
      }
      setSelectedKeywordForTrends(keyword);
    } catch (error) {
      console.error('Error fetching trend data:', error);
      setTrendData([]);
      setComparisonData(null);
    }
  }

  async function fetchYearOverYearComparison(keyword: string, locationCode: number) {
    try {
      const currentYearStart = new Date(customStartDate);
      const currentYearEnd = new Date(customEndDate);
      const currentYear = currentYearStart.getFullYear();
      
      // Calculate previous year dates (same month/day range)
      const previousYearStart = new Date(currentYearStart);
      previousYearStart.setFullYear(currentYear - 1);
      const previousYearEnd = new Date(currentYearEnd);
      previousYearEnd.setFullYear(currentYear - 1);

      // Fetch current year data
      const { data: currentData, error: currentError } = await supabase
        .from('dataforseo_keyword_trends')
        .select('month, search_volume')
        .eq('keyword', keyword)
        .eq('location_code', locationCode)
        .gte('month', currentYearStart.toISOString().split('T')[0])
        .lte('month', currentYearEnd.toISOString().split('T')[0])
        .order('month', { ascending: true });

      if (currentError) throw currentError;

      // Fetch previous year data
      const { data: previousData, error: previousError } = await supabase
        .from('dataforseo_keyword_trends')
        .select('month, search_volume')
        .eq('keyword', keyword)
        .eq('location_code', locationCode)
        .gte('month', previousYearStart.toISOString().split('T')[0])
        .lte('month', previousYearEnd.toISOString().split('T')[0])
        .order('month', { ascending: true });

      if (previousError) throw previousError;

      // Format data for comparison
      const currentYearData = (currentData || []).map(item => ({
        month: new Date(item.month).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        search_volume: item.search_volume || 0,
      }));

      const previousYearData = (previousData || []).map(item => ({
        month: new Date(item.month).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        search_volume: item.search_volume || 0,
      }));

      // Calculate totals and changes
      const currentTotal = currentYearData.reduce((sum, d) => sum + d.search_volume, 0);
      const previousTotal = previousYearData.reduce((sum, d) => sum + d.search_volume, 0);
      const absoluteChange = currentTotal - previousTotal;
      const percentChange = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;

      setComparisonData({
        currentYear: currentYearData,
        previousYear: previousYearData,
        percentChange,
        absoluteChange,
      });
      setTrendData([]);
    } catch (error) {
      console.error('Error fetching year-over-year comparison:', error);
      setComparisonData(null);
    }
  }

  // Process keywords with filters
  const processedKeywords = React.useMemo(() => {
    return keywordData
      .filter(row => {
        // Basic filters
        if (searchTerm && !row.keyword.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        if (priorityFilter !== 'all' && row.dataforseo_keyword_opportunities?.priority !== priorityFilter) return false;
        
        // Advanced filters
        if (minVolume > 0 && (row.search_volume || 0) < minVolume) return false;
        if (maxCpc < 999 && (row.cpc || 0) > maxCpc) return false;
        
        // Multi-location filter
        if (multiLocationMode && selectedLocations.length > 0) {
          if (!selectedLocations.includes(row.location_code)) return false;
        }
        
        // Date range filter (for last_updated)
        if (dateRangeFilter.enabled && dateRangeFilter.startDate && dateRangeFilter.endDate) {
          if (!row.last_updated) return false;
          const updatedDate = new Date(row.last_updated);
          const startDate = new Date(dateRangeFilter.startDate);
          const endDate = new Date(dateRangeFilter.endDate);
          if (updatedDate < startDate || updatedDate > endDate) return false;
        }
        
        return true;
      })
      .map(row => {
        // Compute historical period volume when dateRangeFilter is enabled
        let effectiveVolume = row.search_volume || 0;
        if (
          dateRangeFilter.enabled &&
          dateRangeFilter.startDate &&
          dateRangeFilter.endDate &&
          Array.isArray(row.monthly_searches)
        ) {
          const startYM = String(dateRangeFilter.startDate).slice(0, 7).replace('-', '');
          const endYM = String(dateRangeFilter.endDate).slice(0, 7).replace('-', '');
          effectiveVolume = row.monthly_searches.reduce((sum: number, ms: any) => {
            const ym = String(ms?.month || '').slice(0, 7).replace('-', '');
            const vol = typeof ms?.search_volume === 'number' ? ms.search_volume : 0;
            return ym && ym >= startYM && ym <= endYM ? sum + vol : sum;
          }, 0);
        }

        return {
          keyword: row.keyword,
          search_volume: effectiveVolume,
          cpc: row.cpc || 0,
          competition_level: row.competition_level || 'UNKNOWN',
          keyword_difficulty: row.keyword_difficulty || 0,
          search_intent: row.search_intent || 'unknown',
          opportunity_score: row.dataforseo_keyword_opportunities?.opportunity_score || 0,
          opportunity_type: row.dataforseo_keyword_opportunities?.opportunity_type || 'standard',
          priority: row.dataforseo_keyword_opportunities?.priority || 'LOW',
          location_name: row.location_name || 'Unknown',
          location_code: row.location_code,
          monthly_searches: row.monthly_searches,
        };
      });
  }, [keywordData, searchTerm, priorityFilter, minVolume, maxCpc, multiLocationMode, selectedLocations, dateRangeFilter]);

  // Sort keywords
  const sortedKeywords = React.useMemo(() => {
    return [...processedKeywords].sort((a, b) => {
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      if (sortBy === 'volume') {
        return (a.search_volume - b.search_volume) * multiplier;
      } else {
        return (a.opportunity_score - b.opportunity_score) * multiplier;
      }
    });
  }, [processedKeywords, sortBy, sortOrder]);

  // Calculate summary stats
  const totalVolume = processedKeywords.reduce((sum, k) => sum + k.search_volume, 0);
  const avgVolume = processedKeywords.length > 0 ? Math.round(totalVolume / processedKeywords.length) : 0;
  const highOpportunities = processedKeywords.filter(k => k.opportunity_score > 70).length;
  const avgCpc = processedKeywords.length > 0 
    ? processedKeywords.reduce((sum, k) => sum + k.cpc, 0) / processedKeywords.length 
    : 0;

  function toggleSort(field: 'volume' | 'opportunity') {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  }

  function getCompetitionColor(competition: string) {
    switch (competition.toUpperCase()) {
      case 'HIGH': return 'text-red-600 bg-red-50';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50';
      case 'LOW': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  function getPriorityColor(priority: string) {
    switch (priority) {
      case 'HIGH': return 'text-green-600 bg-green-50';
      case 'MEDIUM': return 'text-blue-600 bg-blue-50';
      case 'LOW': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  function getIntentBadgeColor(intent: string) {
    switch (intent.toLowerCase()) {
      case 'commercial': return 'bg-purple-100 text-purple-800';
      case 'transactional': return 'bg-green-100 text-green-800';
      case 'informational': return 'bg-blue-100 text-blue-800';
      case 'navigational': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-end mb-4">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="text-blue-600" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">Keyword Demand Tracking</h1>
        </div>
        <p className="text-gray-600">
          Geographic keyword research with historical trends - powered by DataForSEO
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Keywords</span>
            <Search className="text-blue-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{processedKeywords.length}</div>
          <div className="text-sm text-gray-500 mt-1">
            {selectedLocation === 'all' ? 'All locations' : locations.find(l => l.location_code === selectedLocation)?.location_name || 'Unknown'}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Monthly Volume</span>
            <BarChart3 className="text-green-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalVolume.toLocaleString()}</div>
          <div className="text-sm text-gray-500 mt-1">Searches/month</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Avg CPC</span>
            <DollarSign className="text-purple-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">${avgCpc.toFixed(2)}</div>
          <div className="text-sm text-gray-500 mt-1">Cost per click</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">High Priority</span>
            <Target className="text-orange-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{highOpportunities}</div>
          <div className="text-sm text-gray-500 mt-1">Score &gt; 70/100</div>
        </div>
      </div>

      {/* View Mode & Visualization Controls */}
      <div className="mb-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1 bg-white">
            <button
              onClick={() => setViewMode('dataforseo')}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'dataforseo'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Keyword Research (DataForSEO)
            </button>
            <button
              onClick={() => setViewMode('google-ads')}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'google-ads'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Active Campaign Keywords (Google Ads)
            </button>
          </div>

          {viewMode === 'dataforseo' && (
            <div className="flex items-center gap-2">
              {/* Visualization Mode Toggle */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1 bg-white">
                <button
                  onClick={() => setVisualizationMode('table')}
                  className={`p-2 rounded transition-colors ${
                    visualizationMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Table View"
                >
                  <BarChart3 size={16} />
                </button>
                <button
                  onClick={() => setVisualizationMode('scatter')}
                  className={`p-2 rounded transition-colors ${
                    visualizationMode === 'scatter'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Scatter Plot"
                >
                  <Target size={16} />
                </button>
                <button
                  onClick={() => setVisualizationMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    visualizationMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Grid View"
                >
                  <Grid3x3 size={16} />
                </button>
              </div>

              {/* Export Button */}
              <button
                onClick={() => {
                  const csv = [
                    ['Keyword', 'Location', 'Volume', 'CPC', 'Competition', 'Difficulty', 'Intent', 'Opportunity Score', 'Priority'],
                    ...sortedKeywords.map(kw => [
                      kw.keyword,
                      kw.location_name,
                      kw.search_volume,
                      kw.cpc,
                      kw.competition_level,
                      kw.keyword_difficulty,
                      kw.search_intent,
                      kw.opportunity_score,
                      kw.priority
                    ])
                  ].map(row => row.join(',')).join('\n');
                  
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `keyword-demand-${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download size={16} />
                Export CSV
              </button>

              {/* Save Preset Button */}
              <button
                onClick={() => setShowSavePresetModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Save size={16} />
                Save Preset
              </button>
            </div>
          )}
        </div>

        {/* Quick Filter Chips */}
        {viewMode === 'dataforseo' && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <Filter size={14} />
              Quick Filters:
            </span>
            <button
              onClick={() => {
                setMinVolume(500);
                setMaxCpc(5);
                setPriorityFilter('HIGH');
              }}
              className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors flex items-center gap-1"
            >
              <Zap size={12} />
              Quick Wins
            </button>
            <button
              onClick={() => {
                setMinVolume(1000);
                setDifficultyRange([0, 30]);
              }}
              className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors flex items-center gap-1"
            >
              <TrendingUp size={12} />
              High Volume Low Difficulty
            </button>
            <button
              onClick={() => {
                setMaxCpc(3);
                setMinVolume(200);
              }}
              className="px-3 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full hover:bg-orange-200 transition-colors flex items-center gap-1"
            >
              <DollarSign size={12} />
              Affordable Keywords
            </button>
            <button
              onClick={() => {
                setSearchIntentFilter(['transactional', 'commercial']);
                setMinVolume(100);
              }}
              className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 transition-colors flex items-center gap-1"
            >
              <Award size={12} />
              Buyer Intent
            </button>
            {(minVolume > 0 || maxCpc < 999 || priorityFilter !== 'all' || searchIntentFilter.length > 0) && (
              <button
                onClick={() => {
                  setMinVolume(0);
                  setMaxCpc(999);
                  setPriorityFilter('all');
                  setSearchIntentFilter([]);
                  setDifficultyRange([0, 100]);
                }}
                className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <X size={12} />
                Clear Quick Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Google Ads Performance View */}
      {viewMode === 'google-ads' && (
        <div className="mb-8">
          {loadingGoogleAds ? (
            <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-md">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : googleAdsKeywords.length > 0 ? (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Active Google Ads Keywords (Last 30 Days)</h3>
                  <p className="text-sm text-gray-600 mt-1">Actual performance from your running campaigns</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="hidden md:flex items-center gap-2">
                    <input
                      type="date"
                      value={googleAdsDateRange.startDate}
                      onChange={(e) => setGoogleAdsDateRange({ ...googleAdsDateRange, startDate: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-1 text-sm"
                    />
                    <span className="text-gray-400">to</span>
                    <input
                      type="date"
                      value={googleAdsDateRange.endDate}
                      onChange={(e) => setGoogleAdsDateRange({ ...googleAdsDateRange, endDate: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-1 text-sm"
                    />
                  </div>
                  <button
                    onClick={fetchGoogleAdsKeywords}
                    disabled={loadingGoogleAds}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      loadingGoogleAds
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-800 text-white hover:bg-gray-900'
                    }`}
                  >
                    <RefreshCw size={16} className={loadingGoogleAds ? 'animate-spin' : ''} />
                    {loadingGoogleAds ? 'Loading...' : 'Refresh'}
                  </button>
                  <button
                    onClick={collectGoogleAdsData}
                    disabled={loadingGoogleAds || collectingGoogleAds}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      loadingGoogleAds || collectingGoogleAds
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <RefreshCw size={16} className={collectingGoogleAds ? 'animate-spin' : ''} />
                    {collectingGoogleAds ? 'Collecting...' : 'Collect & Save'}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CTR</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg CPC</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quality Score</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Bid</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {googleAdsKeywords.map((kw, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{kw.keyword}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{kw.impressions.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{kw.clicks.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{kw.ctr.toFixed(2)}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${kw.avgCpc.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${kw.cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{kw.conversions}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className={`font-bold ${
                            kw.qualityScore >= 7 ? 'text-green-600' : 
                            kw.qualityScore >= 5 ? 'text-yellow-600' : 
                            'text-red-600'
                          }`}>
                            {kw.qualityScore}/10
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${kw.currentBid.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Google Ads Data Available</h3>
              <p className="text-gray-600 mb-6">Click the button below to collect Google Ads campaign keyword performance data</p>
              <button
                onClick={collectGoogleAdsData}
                disabled={loadingGoogleAds || collectingGoogleAds}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  loadingGoogleAds || collectingGoogleAds
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <RefreshCw size={20} className={collectingGoogleAds ? 'animate-spin' : ''} />
                {collectingGoogleAds ? 'Collecting Data...' : 'Collect Google Ads Data'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scatter Plot Visualization */}
      {viewMode === 'dataforseo' && visualizationMode === 'scatter' && sortedKeywords.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Keyword Opportunity Matrix</h3>
              <p className="text-sm text-gray-600">Volume vs. Difficulty - Bubble size = CPC</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-gray-600">High Priority</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-gray-600">Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span className="text-gray-600">Low</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="search_volume" 
                name="Search Volume"
                label={{ value: 'Monthly Search Volume', position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                type="number" 
                dataKey="keyword_difficulty" 
                name="Difficulty"
                label={{ value: 'Keyword Difficulty', angle: -90, position: 'insideLeft' }}
                domain={[0, 100]}
              />
              <ZAxis type="number" dataKey="cpc" range={[50, 400]} name="CPC" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
                        <p className="font-bold text-gray-900 mb-2">{data.keyword}</p>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-gray-600">Volume:</span> <span className="font-semibold">{data.search_volume.toLocaleString()}</span>/mo</p>
                          <p><span className="text-gray-600">CPC:</span> <span className="font-semibold">${data.cpc.toFixed(2)}</span></p>
                          <p><span className="text-gray-600">Difficulty:</span> <span className="font-semibold">{data.keyword_difficulty}/100</span></p>
                          <p><span className="text-gray-600">Intent:</span> <span className="font-semibold capitalize">{data.search_intent}</span></p>
                          <p><span className="text-gray-600">Opportunity Score:</span> <span className="font-semibold">{data.opportunity_score}/100</span></p>
                          <p><span className="text-gray-600">Location:</span> <span className="font-semibold">{data.location_name.split(',')[0]}</span></p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Scatter
                name="High Priority"
                data={sortedKeywords.filter(k => k.priority === 'HIGH')}
                fill="#22c55e"
              />
              <Scatter
                name="Medium Priority"
                data={sortedKeywords.filter(k => k.priority === 'MEDIUM')}
                fill="#3b82f6"
              />
              <Scatter
                name="Low Priority"
                data={sortedKeywords.filter(k => k.priority === 'LOW')}
                fill="#9ca3af"
              />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>How to read this chart:</strong> Keywords in the <strong>bottom-right quadrant</strong> (high volume, low difficulty) represent the best opportunities. 
              Larger bubbles indicate higher CPC. Click any data point to see full keyword details.
            </p>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'dataforseo' && visualizationMode === 'grid' && sortedKeywords.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {sortedKeywords.slice(0, 24).map((keyword, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{keyword.keyword}</h4>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin size={12} />
                    {keyword.location_name.split(',')[0]}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(keyword.priority)}`}>
                  {keyword.priority}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-600">Volume</p>
                  <p className="text-lg font-bold text-gray-900">{keyword.search_volume.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">CPC</p>
                  <p className="text-lg font-bold text-gray-900">${keyword.cpc.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Difficulty</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          keyword.keyword_difficulty < 30 ? 'bg-green-500' :
                          keyword.keyword_difficulty < 70 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${keyword.keyword_difficulty}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{keyword.keyword_difficulty}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Opportunity</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${keyword.opportunity_score}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{keyword.opportunity_score}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getIntentBadgeColor(keyword.search_intent)}`}>
                  {keyword.search_intent}
                </span>
                <button
                  onClick={() => fetchTrendData(keyword.keyword, keyword.location_code)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                >
                  <LineChart size={14} />
                  View Trends
                </button>
              </div>
            </div>
          ))}
          {sortedKeywords.length > 24 && (
            <div className="col-span-full text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-600">
                Showing first 24 keywords. Apply filters or switch to table view to see all {sortedKeywords.length} keywords.
              </p>
            </div>
          )}
        </div>
      )}

      {/* DataForSEO View - Filters Section */}
      {viewMode === 'dataforseo' && visualizationMode === 'table' && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          {/* Refresh Button & Filters */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters & Actions</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddKeywordModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Search size={16} />
                  Add Keywords
                </button>
                <button
                  onClick={refreshKeywordData}
                  disabled={isRefreshing}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isRefreshing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh Keyword Data'}
                </button>
              </div>
        </div>

        {isRefreshing && refreshProgress.total > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">
                {refreshProgress.status}
              </span>
              <span className="text-sm text-blue-700">
                {refreshProgress.current} / {refreshProgress.total}
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(refreshProgress.current / refreshProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MapPin size={16} />
              Geographic Location
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={multiLocationMode}
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.location_code} value={loc.location_code}>
                  {loc.location_name.split(',')[0]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priorities</option>
              <option value="HIGH">High Priority</option>
              <option value="MEDIUM">Medium Priority</option>
              <option value="LOW">Low Priority</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'volume' | 'opportunity')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="opportunity">Opportunity Score</option>
              <option value="volume">Search Volume</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Keywords</label>
            <input
              type="text"
              placeholder="Filter by keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <div className="mt-4">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            {showAdvancedFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
          </button>
        </div>

        {/* Advanced Filters Section */}
        {showAdvancedFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">Advanced Filters</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Multi-Location Comparison */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Multi-Location Comparison</label>
                  <button
                    onClick={() => {
                      setMultiLocationMode(!multiLocationMode);
                      if (!multiLocationMode) {
                        setSelectedLocations([]);
                      } else {
                        setSelectedLocation('all');
                      }
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      multiLocationMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {multiLocationMode ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                {multiLocationMode && (
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                    {locations.map(loc => (
                      <label key={loc.location_code} className="flex items-center gap-2 text-sm hover:bg-gray-50 p-1 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(loc.location_code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLocations([...selectedLocations, loc.location_code]);
                            } else {
                              setSelectedLocations(selectedLocations.filter(id => id !== loc.location_code));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{loc.location_name.split(',')[0]}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Volume & CPC Filters */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Monthly Volume
                  </label>
                  <input
                    type="number"
                    value={minVolume}
                    onChange={(e) => setMinVolume(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum CPC ($)
                  </label>
                  <input
                    type="number"
                    value={maxCpc}
                    onChange={(e) => setMaxCpc(parseFloat(e.target.value) || 999)}
                    min="0"
                    step="0.1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 10.00"
                  />
                </div>
              </div>

              {/* Date Range Filter for Data Collection */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Historical Date Range Collection</label>
                  <button
                    onClick={() => {
                      setDateRangeFilter({ ...dateRangeFilter, enabled: !dateRangeFilter.enabled });
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      dateRangeFilter.enabled
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {dateRangeFilter.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                {dateRangeFilter.enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={dateRangeFilter.startDate}
                        onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, startDate: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        value={dateRangeFilter.endDate}
                        onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, endDate: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Use this to filter displayed data by when it was last updated, or to collect historical data for specific periods.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Reset Filters */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setMinVolume(0);
                  setMaxCpc(999);
                  setMultiLocationMode(false);
                  setSelectedLocations([]);
                  setDateRangeFilter({ enabled: false, startDate: '', endDate: '' });
                  setSelectedLocation('all');
                }}
                className="text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                Reset All Filters
              </button>
            </div>
          </div>
      )}

      {/* Trend Visualization */}
      {selectedKeywordForTrends && (trendData.length > 0 || comparisonData) && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <LineChart className="text-blue-600" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Search Volume Trend</h3>
                <p className="text-sm text-gray-600">
                  "{selectedKeywordForTrends}" - 
                  {comparisonMode === 'yoy' && customStartDate && customEndDate
                    ? ` ${new Date(customStartDate).toLocaleDateString()} to ${new Date(customEndDate).toLocaleDateString()}`
                    : ` Last ${timeRange} months`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Comparison Mode Toggle */}
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => {
                    setComparisonMode('standard');
                    if (selectedKeywordForTrends) {
                      const kw = processedKeywords.find(k => k.keyword === selectedKeywordForTrends);
                      if (kw) fetchTrendData(selectedKeywordForTrends, kw.location_code);
                    }
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    comparisonMode === 'standard'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Standard
                </button>
                <button
                  onClick={() => setComparisonMode('yoy')}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    comparisonMode === 'yoy'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  YoY Compare
                </button>
              </div>

              {comparisonMode === 'standard' && (
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <select
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value as '3' | '6' | '12');
                      if (selectedKeywordForTrends) {
                        const kw = processedKeywords.find(k => k.keyword === selectedKeywordForTrends);
                        if (kw) fetchTrendData(selectedKeywordForTrends, kw.location_code);
                      }
                    }}
                    className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                  </select>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedKeywordForTrends(null);
                  setTrendData([]);
                  setComparisonData(null);
                  setComparisonMode('standard');
                }}
                className="ml-2 text-sm text-gray-500 hover:text-gray-700"
              >
                ✕ Close
              </button>
            </div>
          </div>

          {/* Year-over-Year Date Range Selector */}
          {comparisonMode === 'yoy' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <button
                    onClick={() => {
                      if (selectedKeywordForTrends && customStartDate && customEndDate) {
                        const kw = processedKeywords.find(k => k.keyword === selectedKeywordForTrends);
                        if (kw) fetchTrendData(selectedKeywordForTrends, kw.location_code);
                      }
                    }}
                    disabled={!customStartDate || !customEndDate}
                    className={`w-full px-4 py-2 rounded-lg font-medium ${
                      customStartDate && customEndDate
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Compare Years
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Metrics */}
          {comparisonData && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium mb-1">
                  {new Date(customStartDate).getFullYear()} Total
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {comparisonData.currentYear.reduce((sum, d) => sum + d.search_volume, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 font-medium mb-1">
                  {new Date(customStartDate).getFullYear() - 1} Total
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {comparisonData.previousYear.reduce((sum, d) => sum + d.search_volume, 0).toLocaleString()}
                </div>
              </div>
              <div className={`rounded-lg p-4 ${
                comparisonData.percentChange >= 0 ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <div className={`text-sm font-medium mb-1 ${
                  comparisonData.percentChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  Year-over-Year Change
                </div>
                <div className={`text-2xl font-bold ${
                  comparisonData.percentChange >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {comparisonData.percentChange >= 0 ? '+' : ''}{comparisonData.percentChange.toFixed(1)}%
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {comparisonData.absoluteChange >= 0 ? '+' : ''}{comparisonData.absoluteChange.toLocaleString()} searches
                </div>
              </div>
            </div>
          )}

          {/* Chart */}
          <ResponsiveContainer width="100%" height={300}>
            {comparisonData ? (
              <RechartsLineChart
                data={comparisonData.currentYear.map((curr, idx) => ({
                  month: curr.month,
                  currentYear: curr.search_volume,
                  previousYear: comparisonData.previousYear[idx]?.search_volume || 0,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="currentYear"
                  stroke="#2563eb"
                  strokeWidth={2}
                  name={`${new Date(customStartDate).getFullYear()}`}
                />
                <Line
                  type="monotone"
                  dataKey="previousYear"
                  stroke="#9ca3af"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name={`${new Date(customStartDate).getFullYear() - 1}`}
                />
              </RechartsLineChart>
            ) : (
              <RechartsLineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="search_volume" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  name="Search Volume"
                />
              </RechartsLineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Keywords Table */}
      {sortedKeywords.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12">
          <div className="max-w-2xl mx-auto text-center">
            <AlertCircle className="mx-auto text-amber-500 mb-6" size={64} />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">No keyword data available</h3>
            <p className="text-gray-600 mb-8 text-lg">
              {selectedLocation === 'all' 
                ? 'Click the button below to collect keyword research data for all locations.'
                : 'No keywords found for the selected location. Try selecting a different location or collect data.'}
            </p>
            
            <button
              onClick={refreshKeywordData}
              disabled={isRefreshing}
              className={`inline-flex items-center gap-2 px-8 py-4 text-lg rounded-lg font-medium transition-colors shadow-lg ${
                isRefreshing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl'
              }`}
            >
              <RefreshCw size={24} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Collecting Data...' : 'Collect Keyword Data Now'}
            </button>

            {isRefreshing && refreshProgress.total > 0 && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">
                    {refreshProgress.status}
                  </span>
                  <span className="text-sm text-blue-700">
                    {refreshProgress.current} / {refreshProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(refreshProgress.current / refreshProgress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 text-left">
              <h4 className="font-semibold text-gray-900 mb-3">What happens when you collect data:</h4>
              <ol className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>Connects to DataForSEO API using credentials from your environment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>Collects keyword search volume, CPC, competition, and difficulty data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>Processes data for all active Florida locations (takes ~5-10 minutes)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">4.</span>
                  <span>Automatically displays results when complete</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keyword
                  </th>
                  {selectedLocation === 'all' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('volume')}
                  >
                    <div className="flex items-center gap-1">
                      Volume
                      {sortBy === 'volume' && (
                        sortOrder === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CPC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Competition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Intent
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('opportunity')}
                  >
                    <div className="flex items-center gap-1">
                      Score
                      {sortBy === 'opportunity' && (
                        sortOrder === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trends
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedKeywords.map((keyword, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Search size={16} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{keyword.keyword}</span>
                      </div>
                    </td>
                    {selectedLocation === 'all' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {keyword.location_name.split(',')[0]}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">
                        {keyword.search_volume.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        ${keyword.cpc.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCompetitionColor(keyword.competition_level)}`}>
                        {keyword.competition_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {keyword.keyword_difficulty}/100
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getIntentBadgeColor(keyword.search_intent)}`}>
                        {keyword.search_intent}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">
                        {keyword.opportunity_score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(keyword.priority)}`}>
                        {keyword.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => fetchTrendData(keyword.keyword, keyword.location_code)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                      >
                        <LineChart size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Keywords Modal */}
      {showAddKeywordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Manage Keywords</h3>
                <button
                  onClick={() => {
                    setShowAddKeywordModal(false);
                    setNewKeyword('');
                    setBulkKeywords('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Single Keyword Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Single Keyword
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                    placeholder="e.g., hvac service near me"
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={addKeyword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Bulk Keywords Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Multiple Keywords (one per line)
                </label>
                <textarea
                  value={bulkKeywords}
                  onChange={(e) => setBulkKeywords(e.target.value)}
                  placeholder="hvac service near me&#10;air conditioner cleaning&#10;furnace tune up"
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={addBulkKeywords}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add All
                </button>
              </div>

              {/* Custom Keywords List */}
              {customKeywords.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Your Custom Keywords ({customKeywords.length})
                    </label>
                    <button
                      onClick={() => {
                        if (confirm('Remove all custom keywords?')) {
                          saveCustomKeywords([]);
                        }
                      }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-60 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {customKeywords.map((keyword, idx) => (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          <span>{keyword}</span>
                          <button
                            onClick={() => removeCustomKeyword(keyword)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Core Keywords Display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Core HVAC Keywords ({CORE_KEYWORDS.length}) - Predefined
                </label>
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-40 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {CORE_KEYWORDS.map((keyword, idx) => (
                      <div
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm"
                      >
                        {keyword}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">How it works:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Custom keywords are saved in your browser</li>
                      <li>When you click "Refresh Keyword Data", both core and custom keywords will be collected</li>
                      <li>Data is fetched for all active locations from DataForSEO</li>
                      <li>Keywords are case-insensitive and automatically lowercased</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total Keywords: <span className="font-semibold text-gray-900">{getAllKeywords().length}</span>
                  <span className="mx-2">|</span>
                  Core: <span className="font-semibold">{CORE_KEYWORDS.length}</span>
                  <span className="mx-2">|</span>
                  Custom: <span className="font-semibold">{customKeywords.length}</span>
                </div>
                <button
                  onClick={() => {
                    setShowAddKeywordModal(false);
                    setNewKeyword('');
                    setBulkKeywords('');
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">Geographic & Temporal Analysis Features</h4>
            <p className="text-sm text-blue-800 mb-2">
              This page provides location-specific keyword research with historical trend analysis:
            </p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Geographic Filtering:</strong> View keywords by specific Florida cities or all locations combined</li>
              <li>• <strong>Time Period Analysis:</strong> Click "View" in the Trends column to see 3, 6, or 12-month search volume trends</li>
              <li>• <strong>Multi-Location Comparison:</strong> Switch between locations to identify regional differences</li>
              <li>• <strong>Opportunity Scoring:</strong> Scores combine volume, difficulty, and CPC for prioritization</li>
              <li>• <strong>Historical Data:</strong> Monthly search volumes automatically tracked over time</li>
              <li>• <strong>Custom Keywords:</strong> Click "Add Keywords" to track any additional keywords you want</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
